// Sidebar — 262px white sidebar, view switcher, nav groups, Beyond AI promo, user widget.
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    ChevronsLeft, ChevronDown, Lock, Building2, Sparkles, Settings,
} from "lucide-react";
import { NAV, PORTAL_NAV_SITE, PORTAL_NAV_ADMIN, ROLE_LABELS } from "@/lib/constants";
import { enabledPortalRouteSet } from "@/lib/portalModules";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { useNotifications } from "@/context/NotificationContext";
import { TID } from "@/lib/testIds";
import prMonogram from "@/assets/priority-risk-monogram.png";

export default function Sidebar() {
    const { user } = useAuth();
    const { isInternalView, portalClient, canSwitch, enterPortal, exitPortal, clients } = useView();
    const { byModule, markModuleRead } = useNotifications();
    const [collapsed, setCollapsed] = useState(false);
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const [adminOpen, setAdminOpen] = useState(false);
    const location = useLocation();

    // Per-group collapsed state (internal rail only), persisted to localStorage.
    // Default OPEN: the top group + "Projects & Delivery"; all others collapsed.
    const [navCollapsed, setNavCollapsed] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem("pr.nav.collapsed") || "null");
            if (saved && typeof saved === "object") return saved;
        } catch { /* ignore */ }
        const def = {};
        NAV.forEach((g) => {
            if (!g.group || g.group === "Admin") return;
            def[g.group] = g.group !== "Projects & Delivery";
        });
        return def;
    });
    const toggleGroup = (name) => setNavCollapsed((prev) => {
        const next = { ...prev, [name]: !prev[name] };
        try { localStorage.setItem("pr.nav.collapsed", JSON.stringify(next)); } catch { /* ignore */ }
        return next;
    });

    // What modules to show
    const enabledSet = !isInternalView ? enabledPortalRouteSet(portalClient) : null;
    let visibleGroups;
    if (isInternalView) {
        // Internal: full grouped rail (unchanged).
        visibleGroups = NAV.filter((g) => g.group !== "Admin");
    } else {
        // Portal (Phase 2): a flat, ungrouped, task-first set — chosen by role.
        // client_admin (client director/manager) and internal PR staff previewing a
        // portal (canSwitch) get the broad client-wide set; genuine site_manager /
        // reviewer portal users get the lean cockpit set. Both still gated by
        // the client's configured portal_modules (null => show all).
        const portalNav = (user?.role === "client_admin" || canSwitch) ? PORTAL_NAV_ADMIN : PORTAL_NAV_SITE;
        const perms = user?.permissions || {};
        const items = portalNav.filter((it) => {
            if (it.id === "weekly-reports") return !!portalClient?.weekly_reports_enabled;
            return (enabledSet === null || enabledSet.has(it.id))
                && (!it.module || perms[it.module] === undefined || perms[it.module] !== "-");
        });
        visibleGroups = [{ group: null, items }];
    }
    visibleGroups = visibleGroups.filter((g) => g.items.length > 0);

    // Admin group is demoted out of the scrolling rail into a footer popover (internal only).
    const adminItems = isInternalView ? (NAV.find((g) => g.group === "Admin")?.items || []) : [];

    const brandColour = !isInternalView && portalClient ? portalClient.branding.primary_colour : "var(--brand-accent, #C75A1B)";
    const brandInitials = !isInternalView && portalClient ? portalClient.branding.logo_initials : "PR";
    const brandName = !isInternalView && portalClient ? portalClient.name : "Priority Risk";
    const brandLogoSrc = !isInternalView && portalClient?.branding?.logo_url
        ? `${process.env.REACT_APP_BACKEND_URL}/api/clients/${portalClient.id}/logo?mode=${user?.mode || "live"}`
        : null;

    return (
        <aside
            data-testid={TID.sidebar}
            className={`relative flex-shrink-0 flex flex-col h-screen sticky top-0 transition-all duration-200 ease-out ${collapsed ? "w-[72px]" : "w-[262px]"
                }`}
            style={{ background: "var(--sidebar-bg)", borderRight: "1px solid rgba(0,0,0,.08)" }}
        >
            {/* Collapse toggle */}
            <button
                data-testid={TID.sidebarToggle}
                onClick={() => setCollapsed((v) => !v)}
                aria-label="Toggle sidebar"
                className="absolute -right-3.5 top-5 w-7 h-7 rounded-full bg-white grid place-items-center z-30 transition-all hover:scale-110"
                style={{ border: "1.5px solid var(--brand-accent, #C75A1B)", boxShadow: "0 2px 8px rgba(200,99,42,.18), 0 0 0 4px #F5F3F0", color: "var(--brand-accent, #C75A1B)" }}
            >
                <ChevronsLeft className={`w-3.5 h-3.5 transition-transform ${collapsed ? "rotate-180" : ""}`} strokeWidth={2.5} />
            </button>

            {/* Brand header — kept white so the Priority Risk logo stays clean */}
            <div className="px-4 pt-4 pb-3 flex items-center gap-3 border-b bg-white" style={{ borderColor: "var(--theme-border)" }}>
                <div
                    className="w-[38px] h-[38px] rounded-[10px] grid place-items-center flex-shrink-0 font-serif text-[17px] overflow-hidden bg-white"
                    style={{ border: `2px solid ${brandColour}`, color: brandColour }}
                >
                    {brandLogoSrc ? (
                        <img src={brandLogoSrc} alt={brandName} className="w-full h-full object-contain" />
                    ) : isInternalView ? (
                        <img src={prMonogram} alt="Priority Risk" className="w-full h-full object-contain p-1" />
                    ) : brandInitials}
                </div>
                {!collapsed && (
                    <div className="min-w-0">
                        {isInternalView ? (
                            <>
                                <div className="font-serif-italic text-[19px] leading-none text-[#1F1F1F]">
                                    Priority <i style={{ color: "var(--brand-accent, #C75A1B)" }}>Risk</i>
                                </div>
                                <div className="text-[9.5px] italic mt-1" style={{ color: "#6F6A64", letterSpacing: ".3px" }}>
                                    Management platform
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="text-[17px] font-bold leading-tight text-[#1F1F1F] truncate"
                                     style={{ fontFamily: "var(--portal-heading-font, inherit)" }}>
                                    {brandName}
                                </div>
                                <div className="text-[10px] mt-0.5" style={{ color: "#6F6A64" }}>Client portal</div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* View switcher */}
            {!collapsed && (
                <div className="px-3.5 py-2.5 border-b relative" style={{ borderColor: "rgba(255,255,255,.15)" }}>
                    <div className="text-[9px] font-bold tracking-wider uppercase mb-1.5" style={{ color: "rgba(255,255,255,.6)" }}>
                        Viewing as
                    </div>
                    <button
                        data-testid={TID.viewSwitcherOpen}
                        onClick={() => canSwitch && setSwitcherOpen((v) => !v)}
                        disabled={!canSwitch}
                        className={`w-full px-2.5 py-2 flex items-center gap-2 rounded-lg text-[11.5px] font-semibold transition-colors text-white border border-white/25 bg-white/10 ${canSwitch ? "hover:bg-white/20" : "opacity-90 cursor-default"}`}
                    >
                        <Building2 className="w-3.5 h-3.5" />
                        <span className="flex-1 text-left truncate">
                            {isInternalView ? "Priority Risk Internal" : `Portal · ${portalClient?.name}`}
                        </span>
                        {canSwitch ? <ChevronDown className="w-3.5 h-3.5" /> : <Lock className="w-3 h-3 opacity-50" />}
                    </button>
                    <div className="text-[10px] mt-1.5 pl-1 leading-relaxed" style={{ color: "rgba(255,255,255,.6)" }}>
                        {isInternalView ? "Full internal access · all clients" : `Branded portal · ${portalClient?.name}`}
                    </div>

                    {switcherOpen && canSwitch && (
                        <div
                            data-testid={TID.viewSwitcher}
                            className="absolute left-3.5 right-3.5 top-[100%] mt-1 bg-white border rounded-lg shadow-brand z-40 overflow-hidden animate-fade-in"
                            style={{ borderColor: "#E5E0DA" }}
                        >
                            <button
                                onClick={() => { exitPortal(); setSwitcherOpen(false); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-[12px] hover:bg-[#FBEFE5] hover:text-[#9F4212] ${isInternalView ? "bg-[#FBEFE5] text-[#9F4212] font-semibold" : "text-[#1F1F1F]"
                                    }`}
                            >
                                <Building2 className="w-3.5 h-3.5" />
                                Priority Risk Internal
                            </button>
                            <div className="border-t" style={{ borderColor: "#F0EDE8" }} />
                            <div className="max-h-64 overflow-y-auto" data-testid="view-switcher-client-list">
                                {clients.filter((c) => c.portal_enabled).map((c) => (
                                    <button
                                        key={c.id}
                                        data-testid={`view-switcher-client-${c.slug}`}
                                        onClick={() => { enterPortal(c.id); setSwitcherOpen(false); }}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-[12px] hover:bg-[#F6F4F0] ${portalClient?.id === c.id ? "bg-[#F6F4F0] font-semibold" : ""
                                            }`}
                                    >
                                        <span className="w-3.5 h-3.5 rounded-[3px] shrink-0" style={{ background: c.branding.primary_colour }} />
                                        <span className="truncate text-[#1F1F1F]">{c.name}</span>
                                    </button>
                                ))}
                                {clients.filter((c) => c.portal_enabled).length === 0 && (
                                    <p className="px-3 py-2.5 text-[11.5px] italic" style={{ color: "#9A968F" }}>No clients with the portal enabled yet.</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto px-2.5 pt-2 pb-1">
                {visibleGroups.map((g, gi) => {
                    const isCollapsible = !collapsed && !!g.group;
                    const groupCollapsed = isCollapsible && navCollapsed[g.group];
                    return (
                        <div key={g.group || `top-${gi}`} className="mb-1">
                            {!collapsed && g.group && (
                                isCollapsible ? (
                                    <button
                                        type="button"
                                        data-testid={`nav-group-toggle-${g.group.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                        onClick={() => toggleGroup(g.group)}
                                        className="w-full text-[9.5px] font-bold tracking-wider uppercase pt-3 pb-1 px-2.5 flex items-center justify-between gap-1.5 hover:text-white transition-colors"
                                        style={{ color: "rgba(255,255,255,.6)" }}
                                    >
                                        <span className="flex items-center gap-1.5">
                                            {g.group === "Beyond AI" && <Sparkles className="w-3 h-3" style={{ color: "#fff" }} />}
                                            {g.group}
                                        </span>
                                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${groupCollapsed ? "-rotate-90" : ""}`} />
                                    </button>
                                ) : (
                                    <div className="text-[9.5px] font-bold tracking-wider uppercase pt-3 pb-1 px-2.5 flex items-center gap-1.5"
                                        style={{ color: "rgba(255,255,255,.6)" }}>
                                        {g.group === "Beyond AI" && <Sparkles className="w-3 h-3" style={{ color: "#fff" }} />}
                                        {g.group}
                                    </div>
                                )
                            )}
                            {!groupCollapsed && g.items.map((it) => {
                                const Icon = it.icon;
                                const path = `/app/${it.id}`;
                                const isActive = location.pathname.startsWith(path);
                                const badgeCount = byModule[it.module] || 0;
                                return (
                                    <Link
                                        key={it.id}
                                        to={path}
                                        data-testid={TID.navItem(it.id)}
                                        title={it.label}
                                        onClick={() => { if (badgeCount) markModuleRead(it.module); }}
                                        className={`flex items-center gap-2.5 rounded-lg text-[12.6px] font-medium transition-colors relative ${collapsed ? "justify-center py-2.5 mx-auto w-12" : "px-2.5 py-[7px] w-full"
                                            } ${isActive
                                                ? "text-white font-semibold"
                                                : "text-white/[.88] hover:bg-white/[.12]"
                                            }`}
                                        style={isActive ? {
                                            background: "var(--sidebar-active-bg)",
                                            borderLeft: "4px solid var(--sidebar-active-border)",
                                            paddingLeft: collapsed ? undefined : "calc(0.625rem - 4px)",
                                        } : undefined}
                                    >
                                        {Icon ? <Icon className="w-[15px] h-[15px] flex-shrink-0" /> : null}
                                        {!collapsed && <span className="flex-1 text-left truncate">{it.label}</span>}
                                        {!collapsed && badgeCount > 0 ? (
                                            <span
                                                data-testid={`nav-badge-${it.id}`}
                                                className="text-[10px] font-bold px-1.5 py-px rounded-full"
                                                style={{
                                                    background: "#FFFFFF",
                                                    color: "#B33A2E",
                                                }}
                                            >
                                                {badgeCount > 9 ? "9+" : badgeCount}
                                            </span>
                                        ) : null}
                                    </Link>
                                );
                            })}
                        </div>
                    );
                })}
            </nav>

            {/* Beyond AI promo card — internal view only; pinned below the nav list */}
            {!collapsed && isInternalView && (
                <div
                    className="mx-3 mb-2 p-3 rounded-[12px] bg-ai-grad-soft"
                    style={{ border: "1px solid #BFD8F5" }}
                >
                    <div className="flex items-center gap-1.5 mb-1.5 text-[12.5px] font-bold">
                        <Sparkles className="w-3.5 h-3.5" style={{ color: "#1F6FE5" }} />
                        <b style={{ color: "#1F6FE5" }}>Beyond AI</b>
                        <span style={{ color: "#1F1F1F" }}>assistant</span>
                    </div>
                    <div className="text-[11px] leading-snug mb-2 text-[#2D2D2D]">
                        Ask "what's missing?" on any building, report or BSC.
                    </div>
                    <Link
                        to="/app/ai-assist"
                        data-testid="sidebar-ai-promo-link"
                        className="w-full py-1.5 rounded-md bg-ai-grad text-white text-[11.5px] font-semibold flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity"
                    >
                        <Sparkles className="w-3 h-3" />
                        Open assistant
                    </Link>
                </div>
            )}

            {/* User widget + admin gear popover */}
            {user && (
                <div className="relative flex items-center gap-2.5 px-3.5 py-2.5 border-t" style={{ borderColor: "rgba(255,255,255,.15)" }}>
                    <div
                        className="w-[30px] h-[30px] rounded-full grid place-items-center text-[11px] font-bold flex-shrink-0 text-white"
                        style={{ background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.3)" }}
                    >
                        {user.avatar_initials || user.name?.[0]}
                    </div>
                    {!collapsed && (
                        <div className="min-w-0 flex-1">
                            <div className="text-[12px] font-semibold truncate text-white">{user.name}</div>
                            <div className="text-[10px] truncate" style={{ color: "rgba(255,255,255,.65)" }}>
                                {ROLE_LABELS[user.role]}
                            </div>
                        </div>
                    )}

                    {adminItems.length > 0 && (
                        <>
                            <button
                                type="button"
                                data-testid="sidebar-admin-toggle"
                                onClick={() => setAdminOpen((v) => !v)}
                                aria-label="Admin & settings"
                                title="Admin & settings"
                                className={`w-8 h-8 rounded-lg grid place-items-center flex-shrink-0 transition-colors text-white/80 hover:text-white hover:bg-white/[.14] ${adminOpen ? "bg-white/[.14] text-white" : ""} ${collapsed ? "mx-auto" : ""}`}
                            >
                                <Settings className="w-[17px] h-[17px]" />
                            </button>

                            {adminOpen && (
                                <>
                                    <div className="fixed inset-0 z-30" onClick={() => setAdminOpen(false)} />
                                    <div
                                        data-testid="sidebar-admin-popover"
                                        className="absolute z-40 bottom-[calc(100%+6px)] right-3 left-3 bg-white border rounded-xl shadow-brand overflow-hidden animate-fade-in"
                                        style={{ borderColor: "#E5E0DA" }}
                                    >
                                        <div className="px-3 pt-2.5 pb-1.5 text-[9.5px] font-bold tracking-wider uppercase" style={{ color: "#9A968F" }}>
                                            Admin & settings
                                        </div>
                                        <div className="pb-1.5 max-h-[52vh] overflow-y-auto">
                                            {adminItems.map((it) => {
                                                const Icon = it.icon;
                                                const path = `/app/${it.id}`;
                                                const isActive = location.pathname.startsWith(path);
                                                return (
                                                    <Link
                                                        key={it.id}
                                                        to={path}
                                                        data-testid={TID.navItem(it.id)}
                                                        onClick={() => setAdminOpen(false)}
                                                        className={`flex items-center gap-2.5 px-3 py-2 text-[12.5px] transition-colors ${isActive ? "bg-[#FBEFE5] text-[#9F4212] font-semibold" : "text-[#1F1F1F] hover:bg-[#F6F4F0]"}`}
                                                    >
                                                        {Icon ? <Icon className="w-[15px] h-[15px] flex-shrink-0" style={{ color: isActive ? "#9F4212" : "#6F6A64" }} /> : null}
                                                        <span className="truncate">{it.label}</span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}
        </aside>
    );
}
